/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import { AppState } from 'src/app/store/app.reducer';
import * as NotificationsActions from '../notifications/notifications.actions';
import * as HelpActions from './help.actions';
import { HelpService } from 'src/app/services/help.service';
import { selectCurrentLang } from '../user/user.selector';

@Injectable()
export class HelpEffects {

    getHelp$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.getHelp),
        withLatestFrom(this.store.select('user', 'currentLang')),
        switchMap(([action, currentLang]) => this.helpService.getHelp(currentLang.code.substring(0, 2), action.appid).pipe(
            map((resp) => {
                if (resp.Success) {
                    return HelpActions.getHelpSuccess({ sectionList: resp.Data });
                } else {
                    return HelpActions.getHelpFailed({ errorMessage: this.translate.instant('ERROR_HELP') });
                }
            }),
            catchError(() => of(HelpActions.getHelpFailed({ errorMessage: this.translate.instant('ERROR_HELP') })))
        ))
    ));

    getHelpAll$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.getHelpAll),
        withLatestFrom(this.store.select(selectCurrentLang)),
        switchMap(([ , currentLang]) => this.helpService.getHelpAll(currentLang.code.substring(0, 2)).pipe(
            map((resp) => {
                if (resp.Success) {
                    return HelpActions.getHelpAllSuccess({ sectionList: resp.Data });
                } else {
                    return HelpActions.getHelpAllFailed({ errorMessage: this.translate.instant('ERROR_HELP') });
                }
            }),
            catchError(()  => of(HelpActions.getHelpAllFailed({ errorMessage: this.translate.instant('ERROR_HELP') })))
        ))
    ));

    getHelpAllLangParam$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.getHelpAllLangParam),
        switchMap((action) => this.helpService.getHelpAll(action.lang.code.substring(0, 2)).pipe(
            map((resp) => {
                if (resp.Success) {
                    return HelpActions.getHelpAllLangParamSuccess({ sectionList: resp.Data, viewLangHelpAll: action.lang });
                } else {
                    return HelpActions.getHelpAllFailed({ errorMessage: this.translate.instant('ERROR_HELP') });
                }
            }),
            catchError(()  => of(HelpActions.getHelpAllFailed({ errorMessage: this.translate.instant('ERROR_HELP') })))
        ))
    ));

    getHelpGuest$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.getHelpGuest),
        withLatestFrom(this.store.select('user', 'currentLang')),
        switchMap(([action, currentLang]) => this.helpService.getHelpGuest(currentLang.code.substring(0, 2), action.appid).pipe(
            map((resp) => {
                if (resp.Success) {
                    return HelpActions.getHelpGuestSuccess({ sectionList: resp.Data });
                } else {
                    return HelpActions.getHelpGuestFailed({ errorMessage: this.translate.instant('ERROR_HELP') });
                }
            }),
            catchError(()  => of(HelpActions.getHelpGuestFailed({ errorMessage: this.translate.instant('ERROR_HELP') })))
        ))
    ));

    getHelpGuestLangParam$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.getHelpGuestLangParam),
        switchMap((action) => this.helpService.getHelpGuest(action.lang.code.substring(0, 2), action.appid).pipe(
            map((resp) => {
                if (resp.Success) {
                    return HelpActions.getHelpGuestSuccess({ sectionList: resp.Data });
                } else {
                    return HelpActions.getHelpGuestFailed({ errorMessage: this.translate.instant('ERROR_HELP') });
                }
            }),
            catchError(()  => of(HelpActions.getHelpGuestFailed({ errorMessage: this.translate.instant('ERROR_HELP') })))
        ))
    ));

    getHelpSearch$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.getHelpSearch),
        withLatestFrom(this.store.select('user', 'currentLang')),
        switchMap(([action, currentLang]) => this.helpService.getHelpSearch(currentLang.code.substring(0, 2), action.filter, action.appid).pipe(
            map((resp) => {
                if (resp.Success) {
                    return HelpActions.getHelpSearchSuccess({ subSectionList: resp.Data });
                } else {
                    return HelpActions.getHelpSearchFailed({ errorMessage: this.translate.instant('ERROR_HELP_SEARCH') });
                }
            }),
            catchError(()  => of(HelpActions.getHelpSearchFailed({ errorMessage: this.translate.instant('ERROR_HELP_SEARCH') })))
        ))
    ));

    getHelpGuestSearch$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.getHelpGuestSearch),
        withLatestFrom(this.store.select('user', 'currentLang')),
        switchMap(([action, currentLang]) => this.helpService.getHelpGuestSearch(currentLang.code.substring(0, 2), action.filter, action.appid).pipe(
            map((resp) => {
                if (resp.Success) {
                    return HelpActions.getHelpGuestSearchSuccess({ subSectionList: resp.Data });
                } else {
                    return HelpActions.getHelpGuestSearchFailed({ errorMessage: this.translate.instant('ERROR_HELP') });
                }
            }),
            catchError(()  => of(HelpActions.getHelpGuestSearchFailed({ errorMessage: this.translate.instant('ERROR_HELP') })))
        ))
    ));

    getHelpGuestSearchLangParam$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.getHelpGuestSearchLangParam),
        switchMap((action) => this.helpService.getHelpGuestSearch(action.lang.code.substring(0, 2), action.filter, action.appid).pipe(
            map((resp) => {
                if (resp.Success) {
                    return HelpActions.getHelpGuestSearchSuccess({ subSectionList: resp.Data });
                } else {
                    return HelpActions.getHelpGuestSearchFailed({ errorMessage: this.translate.instant('ERROR_HELP') });
                }
            }),
            catchError(()  => of(HelpActions.getHelpGuestSearchFailed({ errorMessage: this.translate.instant('ERROR_HELP') })))
        ))
    ));

    //Gestione Sezioni
    getHelpSectionAll$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.getHelpSectionAll),
        switchMap(()=> this.helpService.getHelpSectionAll()
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return HelpActions.getHelpSectionAllSuccess({ sectionList: resp.Data});
                    }
                    return HelpActions.getHelpSectionAllFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(HelpActions.getHelpSectionAllFailed({ errorMessage: error.message })))
            )
        )
    ));


    getHelpSections$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.getHelpSections),
        switchMap(()=> this.helpService.getHelpSections()
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return HelpActions.getHelpSectionsSuccess({ sections : resp.Data  });
                    }
                    return HelpActions.getHelpSectionsFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(HelpActions.getHelpSectionsFailed({ errorMessage: error.message })))
            )
        )
    ));

    getHelpSection$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.getHelpSection),
        switchMap((action)=> this.helpService.getHelpSection(action.sectionId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return HelpActions.getHelpSectionSuccess({ section : resp.Data  });
                    }
                    return HelpActions.getHelpSectionFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(HelpActions.getHelpSectionFailed({ errorMessage: error.message })))
            )
        )
    ));

    addHelpSection$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.addHelpSection),
        switchMap((action) => this.helpService.addHelpSection(action.section)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return HelpActions.addHelpSectionSuccess({ message: this.translate.instant('ADD_HELP_SECTION_SUCCESS') });
                    } else {
                        return HelpActions.addHelpSectionFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(HelpActions.addHelpSectionFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                })
            )
        )
    ));

    editHelpSection$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.editHelpSection),
        switchMap((action) => this.helpService.editHelpSection(action.section)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return HelpActions.editHelpSectionSuccess({ message: this.translate.instant('EDIT_HELP_SECTION_SUCCESS') });
                    } else {
                        return HelpActions.editHelpSectionFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(HelpActions.editHelpSectionFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteHelpSection$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.deleteHelpSection),
        switchMap((action) => this.helpService.deleteHelpSection(action.sectionId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return HelpActions.deleteHelpSectionSuccess({ message: this.translate.instant('DELETE_HELP_SECTION_SUCCESS') });
                    } else {
                        return HelpActions.deleteHelpSectionFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(HelpActions.deleteHelpSectionFailed({ errorMessage: error.message })))
            )
        )
    ));

    getHelpSubSections$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.getHelpSubSections),
        switchMap((action)=> this.helpService.getHelpSubSections(action.sectionId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return HelpActions.getHelpSubSectionsSuccess({ subsections : resp.Data  });
                    }
                    return HelpActions.getHelpSubSectionsFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(HelpActions.getHelpSubSectionsFailed({ errorMessage: error.message })))
            )
        )
    ));

    getHelpSubSection$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.getHelpSubSection),
        switchMap((action)=> this.helpService.getHelpSubSection(action.subsectionId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return HelpActions.getHelpSubSectionSuccess({ subsection : resp.Data  });
                    }
                    return HelpActions.getHelpSubSectionFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(HelpActions.getHelpSubSectionFailed({ errorMessage: error.message })))
            )
        )
    ));

    addHelpSubSection$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.addHelpSubSection),
        switchMap((action) => this.helpService.addHelpSubSection(action.subsection)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return HelpActions.addHelpSubSectionSuccess({ message: this.translate.instant('ADD_HELP_SUB_SECTION_SUCCESS') });
                    } else {
                        return HelpActions.addHelpSubSectionFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(HelpActions.addHelpSubSectionFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                })
            )
        )
    ));

    editHelpSubSection$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.editHelpSubSection),
        switchMap((action) => this.helpService.editHelpSubSection(action.subsection)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return HelpActions.editHelpSubSectionSuccess({ message: this.translate.instant('EDIT_HELP_SUB_SECTION_SUCCESS') });
                    } else {
                        return HelpActions.editHelpSubSectionFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(HelpActions.editHelpSubSectionFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteHelpSubSection$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.deleteHelpSubSection),
        switchMap((action) => this.helpService.deleteHelpSubSection(action.subsectionId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return HelpActions.deleteHelpSubSectionSuccess({ message: this.translate.instant('DELETE_HELP_SUB_SECTION_SUCCESS') });
                    } else {
                        return HelpActions.deleteHelpSubSectionFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(HelpActions.deleteHelpSubSectionFailed({ errorMessage: error.message })))
            )
        )
    ));


    getHelpRows$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.getHelpRows),
        switchMap((action)=> this.helpService.getHelpRows(action.subsectionId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return HelpActions.getHelpRowsSuccess({ rows : resp.Data  });
                    }
                    return HelpActions.getHelpRowsFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(HelpActions.getHelpRowsFailed({ errorMessage: error.message })))
            )
        )
    ));

    getHelpRow$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.getHelpRow),
        switchMap((action)=> this.helpService.getHelpRow(action.rowId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return HelpActions.getHelpRowSuccess({ row : resp.Data  });
                    }
                    return HelpActions.getHelpRowFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(HelpActions.getHelpRowFailed({ errorMessage: error.message })))
            )
        )
    ));

    addHelpRow$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.addHelpRow),
        switchMap((action) => this.helpService.addHelpRow(action.row)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return HelpActions.addHelpRowSuccess({ message: this.translate.instant('ADD_HELP_SUB_SECTION_SUCCESS') });
                    } else {
                        return HelpActions.addHelpRowFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(HelpActions.addHelpRowFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                })
            )
        )
    ));

    editHelpRow$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.editHelpRow),
        switchMap((action) => this.helpService.editHelpRow(action.row)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return HelpActions.editHelpRowSuccess({ message: this.translate.instant('EDIT_HELP_SUB_SECTION_SUCCESS') });
                    } else {
                        return HelpActions.editHelpRowFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(HelpActions.editHelpRowFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteHelpRow$ = createEffect(() => this.actions$.pipe(
        ofType(HelpActions.deleteHelpRow),
        switchMap((action) => this.helpService.deleteHelpRow(action.rowId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return HelpActions.deleteHelpRowSuccess({ message: this.translate.instant('DELETE_HELP_SUB_SECTION_SUCCESS') });
                    } else {
                        return HelpActions.deleteHelpRowFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(HelpActions.deleteHelpRowFailed({ errorMessage: error.message })))
            )
        )
    ));

    notifySuccess$ = createEffect(() => this.actions$.pipe(
        ofType(
            HelpActions.addHelpSectionSuccess,
            HelpActions.editHelpSectionSuccess,
            HelpActions.deleteHelpSectionSuccess,
            HelpActions.addHelpSubSectionSuccess,
            HelpActions.editHelpSubSectionSuccess,
            HelpActions.deleteHelpSubSectionSuccess,
            HelpActions.addHelpRowSuccess,
            HelpActions.editHelpRowSuccess,
            HelpActions.deleteHelpRowSuccess,
        ),
        tap((action) => {
            if (action.message === '') {
                return;
            }
            const notification = new Notification("HELP", action.message, NotificationLevel.Success, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    notifyError$ = createEffect(() => this.actions$.pipe(
        ofType(
            HelpActions.getHelpFailed,
            HelpActions.getHelpAllFailed,
            HelpActions.getHelpGuestFailed,
            HelpActions.getHelpSearchFailed,
            HelpActions.getHelpSectionAllFailed,
            HelpActions.getHelpSectionFailed,
            HelpActions.getHelpSectionsFailed,
            HelpActions.addHelpSectionFailed,
            HelpActions.editHelpSectionFailed,
            HelpActions.deleteHelpSectionFailed,
            HelpActions.getHelpSubSectionFailed,
            HelpActions.getHelpSubSectionsFailed,
            HelpActions.addHelpSubSectionFailed,
            HelpActions.editHelpSubSectionFailed,
            HelpActions.deleteHelpSubSectionFailed,
            HelpActions.getHelpRowsFailed,
            HelpActions.getHelpRowFailed,
            HelpActions.addHelpRowFailed,
            HelpActions.editHelpRowFailed,
            HelpActions.deleteHelpRowFailed,
        ),
        tap((action) => {
            if (action.errorMessage === '') {
                return;
            }
            const notification = new Notification("HELP", action.errorMessage, NotificationLevel.Error, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    constructor(
        private actions$: Actions,
        private store: Store<AppState>,
        private translate: TranslateService,
        private helpService: HelpService,
    ) { }
}
